import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UserContext from '../../../../components/User_Context.js';
import * as menu from "../../../../assets/menus/menu.js";
import * as api from "../../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Choices from '../../../../components/Choices.js';
import * as breadcrumb from "../../../../components/ui/breadcrumb.js";
import { formBuilderLabels } from '../../../_label/form_builder/form_builder_labels.js';

function Form_Assignment_Screen() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const { t } = useTranslation();
    const MySwal = withReactContent(Swal);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(null);
    const [selectedUser, setSelectedUser] = useState("");
    const [users, setUsers] = useState([]);
    const [formList, setFormList] = useState([]);
    const [selectedForm, setSelectedForm] = useState(formId || "");
    const [requiresValidation, setRequiresValidation] = useState(false);
    const [selectedUserObject, setSelectedUserObject] = useState(null);
    const [expectedCompilationStartDate, setExpectedCompilationStartDate] = useState("");
    const [expectedCompilationEndDate, setExpectedCompilationEndDate] = useState("");
    const [cycles, setCycles] = useState(1);

    const [mainTab, setMainTab] = useState('assignment'); // 'assignment' or 'search'
    const [activeTab, setActiveTab] = useState('user'); // 'user', 'group', or 'collaborative'
    const [selectedGroup, setSelectedGroup] = useState("");
    const [groups, setGroups] = useState([]);
    const [selectedGroupObject, setSelectedGroupObject] = useState(null);
    const [collaborativeForms, setCollaborativeForms] = useState([]);
    
    // Lazy loading states for user search (assignment tab)
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [isSearchingUsers, setIsSearchingUsers] = useState(false);
    const [showUserSearchResults, setShowUserSearchResults] = useState(false);
    
    // Lazy loading states for group search (assignment tab)
    const [groupSearchTerm, setGroupSearchTerm] = useState('');
    const [groupSearchResults, setGroupSearchResults] = useState([]);
    const [isSearchingGroups, setIsSearchingGroups] = useState(false);
    const [showGroupSearchResults, setShowGroupSearchResults] = useState(false);
    
    // Lazy loading states for search filters
    const [filterUserSearchTerm, setFilterUserSearchTerm] = useState('');
    const [filterUserSearchResults, setFilterUserSearchResults] = useState([]);
    const [isSearchingFilterUsers, setIsSearchingFilterUsers] = useState(false);
    const [showFilterUserSearchResults, setShowFilterUserSearchResults] = useState(false);
    
    const [filterGroupSearchTerm, setFilterGroupSearchTerm] = useState('');
    const [filterGroupSearchResults, setFilterGroupSearchResults] = useState([]);
    const [isSearchingFilterGroups, setIsSearchingFilterGroups] = useState(false);
    const [showFilterGroupSearchResults, setShowFilterGroupSearchResults] = useState(false);

    // Collaborative assignment states
    const [formActors, setFormActors] = useState([]);
    const [organizationalRoles, setOrganizationalRoles] = useState([]);
    const [actorRoleMappings, setActorRoleMappings] = useState({}); // { actorId: [roleCode1, roleCode2, ...] }
    const [businessUnits, setBusinessUnits] = useState([]);
    const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]); // Array of business unit codes
    const [freeAssignments, setFreeAssignments] = useState(false); // Assegnazioni libere
    const [assignmentWithNotification, setAssignmentWithNotification] = useState(false); // Assegnazione con notifica
    const [actorAllUsers, setActorAllUsers] = useState({}); // { actorId: boolean } - Flag "Tutti gli utenti" per ogni attore
    const [actorOrder, setActorOrder] = useState({}); // { actorId: number } - Ordine di compilazione per ogni attore
    const [actorNotificationAutomatic, setActorNotificationAutomatic] = useState({}); // { actorId: boolean } - Notifica automatica attiva
    const [actorNotificationCustom, setActorNotificationCustom] = useState({}); // { actorId: boolean } - Notifica personalizzata attiva
    const [actorNotificationEmails, setActorNotificationEmails] = useState({}); // { actorId: [email1, email2, ...] } - Email personalizzate per ogni attore

    // Search assignments states
    const [searchLoading, setSearchLoading] = useState(false);
    const [assignments, setAssignments] = useState([]);
    const [searchFilters, setSearchFilters] = useState({
        includeInactive: false,
        userId: "",
        groupId: "",
        formId: "",
        startDateFrom: "",
        endDateBefore: "",
        requiresValidation: null
    });

    // Edit modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [editForm, setEditForm] = useState({
        selectedUser: "",
        selectedGroup: "",
        selectedForm: "",
        requiresValidation: false,
        expectedCompilationStartDate: "",
        expectedCompilationEndDate: "",
        cycles: 1,
        activeTab: 'user'
    });

    useEffect(() => {
        (async () => {
            menu.write({ user: currentUser() });
        })();
    }, []);

    // Initialize translations from centralized file
    useEffect(() => {
        const i18n = menu.getI18n();
        i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
        i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);
    }, []);

    // Set breadcrumb
    const setBreadCrumb = () => {
        const formName = form ? form.Name : t('form', { ns: 'labels' });
        
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/finder", name: t('form', { ns: 'labels' }) },
            { link: `/form/assignments/${formId || ''}`, name: `${t('formAssignment', { ns: 'labels' })}: ${formName}` }
        ];
        
        breadcrumb.set(path);
    };

    // Load form data and initial data (not all users/groups - using lazy loading instead)
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                // Note: We no longer load all users and groups here as we use lazy loading.
                // Users and groups will be loaded on-demand as the user types in the search box.

                // Load organizational chart roles for collaborative assignments
                const rolesResult = await api.sendMessage("admin/vocs/get", {
                    user: currentUser(),
                    body: {
                        voc_id: "organizational_chart_roles"
                    }
                });

                if (rolesResult?.success && rolesResult?.items) {
                    setOrganizationalRoles(rolesResult.items);
                }

                const businessUnitsResult = (await api.sendMessage("admin/system/organization/get_business_units", {
                    user: currentUser(),
                }));

                if (businessUnitsResult?.success && businessUnitsResult?.units) {
                    // Transform business units to match Choices component format
                    const formattedBusinessUnits = businessUnitsResult.units.map(bu => ({
                        Code: bu.Id,
                        Name: bu.Name || 'Unnamed Business Unit'
                    }));
                    setBusinessUnits(formattedBusinessUnits);
                }

                // Load forms for dropdown
                const formsResult = await api.sendMessage("darm/formBuilder/finder", {
                    user: currentUser(),
                    body: {}
                });

                if (formsResult?.filtered) {
                    // Transform form data to match Choices component format
                    const formattedForms = formsResult.filtered.map(form => ({
                        Code: form.Id,
                        Name: form.FormName,
                        IsCollaborative: form.IsCollaborative || false
                    }));
                    setFormList(formattedForms);

                    // Filter collaborative forms
                    const collaborativeOnly = formattedForms.filter(form => form.IsCollaborative === true);
                    setCollaborativeForms(collaborativeOnly);
                }

                // If formId is provided, load the specific form
                if (formId) {
                    const formResult = await api.sendMessage("darm/formBuilder/get", {
                        user: currentUser(),
                        body: { id: formId }
                    });

                    if (formResult?.success && formResult?.form) {
                        setForm(formResult.form);
                        setSelectedForm(formId);
                    }
                }
            } catch (error) {
                console.error("Error loading data:", error);
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: error.message || t('error', { ns: 'labels' }),
                    icon: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [formId, currentUser]);

    // Update breadcrumb when form changes
    useEffect(() => {
        setBreadCrumb();
    }, [form]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close user search dropdowns
            if (showUserSearchResults && !event.target.closest('.position-relative')) {
                setShowUserSearchResults(false);
            }
            if (showGroupSearchResults && !event.target.closest('.position-relative')) {
                setShowGroupSearchResults(false);
            }
            if (showFilterUserSearchResults && !event.target.closest('.position-relative')) {
                setShowFilterUserSearchResults(false);
            }
            if (showFilterGroupSearchResults && !event.target.closest('.position-relative')) {
                setShowFilterGroupSearchResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserSearchResults, showGroupSearchResults, showFilterUserSearchResults, showFilterGroupSearchResults]);

    // Load form actors when a collaborative form is selected
    useEffect(() => {
        const loadFormActors = async () => {
            if (activeTab === 'collaborative' && selectedForm) {
                try {
                    const actorsResult = await api.sendMessage("darm/formBuilder/getFormActors", {
                        user: currentUser(),
                        body: { formId: selectedForm }
                    });

                    if (actorsResult?.success && actorsResult?.actors) {
                        setFormActors(actorsResult.actors);

                        // Initialize empty mappings for each actor
                        const initialMappings = {};
                        const initialAllUsers = {};
                        const initialOrder = {};
                        const initialNotificationAutomatic = {};
                        const initialNotificationCustom = {};
                        const initialNotificationEmails = {};
                        actorsResult.actors.forEach((actorData, index) => {
                            initialMappings[actorData.actor.id] = [];
                            initialAllUsers[actorData.actor.id] = false;
                            initialOrder[actorData.actor.id] = index + 1; // Default order: 1, 2, 3, ...
                            initialNotificationAutomatic[actorData.actor.id] = false;
                            initialNotificationCustom[actorData.actor.id] = false;
                            initialNotificationEmails[actorData.actor.id] = [];
                        });
                        setActorRoleMappings(initialMappings);
                        setActorAllUsers(initialAllUsers);
                        setActorOrder(initialOrder);
                        setActorNotificationAutomatic(initialNotificationAutomatic);
                        setActorNotificationCustom(initialNotificationCustom);
                        setActorNotificationEmails(initialNotificationEmails);
                    } else if (!actorsResult?.success) {
                        console.error("Error loading form actors:", actorsResult?.error);
                        setFormActors([]);
                        setActorRoleMappings({});
                        setActorAllUsers({});
                        setActorOrder({});
                        setActorNotificationAutomatic({});
                        setActorNotificationCustom({});
                        setActorNotificationEmails({});
                    }
                } catch (error) {
                    console.error("Error loading form actors:", error);
                    setFormActors([]);
                    setActorRoleMappings({});
                    setActorAllUsers({});
                    setActorOrder({});
                    setActorNotificationAutomatic({});
                    setActorNotificationCustom({});
                    setActorNotificationEmails({});
                }
            } else {
                // Clear actors when not in collaborative tab
                setFormActors([]);
                setActorRoleMappings({});
                setActorAllUsers({});
                setActorOrder({});
                setActorNotificationAutomatic({});
                setActorNotificationCustom({});
                setActorNotificationEmails({});
            }
        };

        loadFormActors();
    }, [activeTab, selectedForm, currentUser]);

    // Debounced search for users
    let userSearchTimer = null;
    
    const searchUsers = async (searchTerm) => {
        if (searchTerm.length < 3) {
            setUserSearchResults([]);
            setShowUserSearchResults(false);
            return;
        }

        setIsSearchingUsers(true);
        
        try {
            const usersResult = await api.sendMessage("admin/security/getAllUsers", {
                user: currentUser(),
                body: { searchTerm: searchTerm }
            });
            
            if (usersResult?.success && usersResult?.users) {
                const formattedUsers = usersResult.users.map(user => {
                    const name = user.Name || '';
                    const surname = user.LastName || '';
                    const email = user.EMail || '';
                    
                    let displayName = name;
                    if (surname) {
                        displayName += ` ${surname}`;
                    }
                    if (email) {
                        displayName += ` (${email})`;
                    }
                    
                    return {
                        Code: user.Id,
                        Name: displayName || email || user.Username || 'ANONYMOUS',
                        OriginalUser: user
                    };
                });
                setUserSearchResults(formattedUsers);
                setShowUserSearchResults(true);
            }
        } catch (error) {
            console.error("Error searching users:", error);
            setUserSearchResults([]);
            setShowUserSearchResults(true);
        } finally {
            setIsSearchingUsers(false);
        }
    };

    const handleUserSearchChange = (value) => {
        setUserSearchTerm(value);
        
        if (userSearchTimer) {
            clearTimeout(userSearchTimer);
        }
        
        userSearchTimer = setTimeout(() => {
            searchUsers(value);
        }, 300);
    };

    const handleSelectUserFromSearch = (user) => {
        setSelectedUser(user.Code);
        setSelectedUserObject(user.OriginalUser);
        setUserSearchTerm('');
        setUserSearchResults([]);
        setShowUserSearchResults(false);
    };

    // Debounced search for groups
    let groupSearchTimer = null;
    
    const searchGroups = async (searchTerm) => {
        if (searchTerm.length < 2) {
            setGroupSearchResults([]);
            setShowGroupSearchResults(false);
            return;
        }

        setIsSearchingGroups(true);
        
        try {
            const groupsResult = await api.sendMessage("admin/security/getGroups", {
                user: currentUser(),
                body: { searchTerm: searchTerm }
            });
            
            if (groupsResult?.success && groupsResult?.groups) {
                const formattedGroups = groupsResult.groups.map(group => ({
                    Code: group.Id,
                    Name: group.Name || 'Unnamed Group',
                    OriginalGroup: group
                }));
                setGroupSearchResults(formattedGroups);
                setShowGroupSearchResults(true);
            }
        } catch (error) {
            console.error("Error searching groups:", error);
            setGroupSearchResults([]);
            setShowGroupSearchResults(true);
        } finally {
            setIsSearchingGroups(false);
        }
    };

    const handleGroupSearchChange = (value) => {
        setGroupSearchTerm(value);
        
        if (groupSearchTimer) {
            clearTimeout(groupSearchTimer);
        }
        
        groupSearchTimer = setTimeout(() => {
            searchGroups(value);
        }, 300);
    };

    const handleSelectGroupFromSearch = (group) => {
        setSelectedGroup(group.Code);
        setSelectedGroupObject(group.OriginalGroup);
        setGroupSearchTerm('');
        setGroupSearchResults([]);
        setShowGroupSearchResults(false);
    };

    // Debounced search for filter users
    let filterUserSearchTimer = null;
    
    const searchFilterUsers = async (searchTerm) => {
        if (searchTerm.length < 3) {
            setFilterUserSearchResults([]);
            setShowFilterUserSearchResults(false);
            return;
        }

        setIsSearchingFilterUsers(true);
        
        try {
            const usersResult = await api.sendMessage("admin/security/getAllUsers", {
                user: currentUser(),
                body: { searchTerm: searchTerm }
            });
            
            if (usersResult?.success && usersResult?.users) {
                const formattedUsers = usersResult.users.map(user => {
                    const name = user.Name || '';
                    const surname = user.LastName || '';
                    const email = user.EMail || '';
                    
                    let displayName = name;
                    if (surname) {
                        displayName += ` ${surname}`;
                    }
                    if (email) {
                        displayName += ` (${email})`;
                    }
                    
                    return {
                        Code: user.Id,
                        Name: displayName || email || user.Username || 'ANONYMOUS'
                    };
                });
                setFilterUserSearchResults(formattedUsers);
                setShowFilterUserSearchResults(true);
            }
        } catch (error) {
            console.error("Error searching filter users:", error);
            setFilterUserSearchResults([]);
            setShowFilterUserSearchResults(true);
        } finally {
            setIsSearchingFilterUsers(false);
        }
    };

    const handleFilterUserSearchChange = (value) => {
        setFilterUserSearchTerm(value);
        
        if (filterUserSearchTimer) {
            clearTimeout(filterUserSearchTimer);
        }
        
        filterUserSearchTimer = setTimeout(() => {
            searchFilterUsers(value);
        }, 300);
    };

    const handleSelectFilterUserFromSearch = (userId) => {
        handleFilterChange('userId', userId);
        setFilterUserSearchTerm('');
        setFilterUserSearchResults([]);
        setShowFilterUserSearchResults(false);
    };

    // Debounced search for filter groups
    let filterGroupSearchTimer = null;
    
    const searchFilterGroups = async (searchTerm) => {
        if (searchTerm.length < 2) {
            setFilterGroupSearchResults([]);
            setShowFilterGroupSearchResults(false);
            return;
        }

        setIsSearchingFilterGroups(true);
        
        try {
            const groupsResult = await api.sendMessage("admin/security/getGroups", {
                user: currentUser(),
                body: { searchTerm: searchTerm }
            });
            
            if (groupsResult?.success && groupsResult?.groups) {
                const formattedGroups = groupsResult.groups.map(group => ({
                    Code: group.Id,
                    Name: group.Name || 'Unnamed Group'
                }));
                setFilterGroupSearchResults(formattedGroups);
                setShowFilterGroupSearchResults(true);
            }
        } catch (error) {
            console.error("Error searching filter groups:", error);
            setFilterGroupSearchResults([]);
            setShowFilterGroupSearchResults(true);
        } finally {
            setIsSearchingFilterGroups(false);
        }
    };

    const handleFilterGroupSearchChange = (value) => {
        setFilterGroupSearchTerm(value);
        
        if (filterGroupSearchTimer) {
            clearTimeout(filterGroupSearchTimer);
        }
        
        filterGroupSearchTimer = setTimeout(() => {
            searchFilterGroups(value);
        }, 300);
    };

    const handleSelectFilterGroupFromSearch = (groupId) => {
        handleFilterChange('groupId', groupId);
        setFilterGroupSearchTerm('');
        setFilterGroupSearchResults([]);
        setShowFilterGroupSearchResults(false);
    };

    // Handle actor role mapping change
    const handleActorRoleChange = (actorId, selectedRoles) => {
        setActorRoleMappings(prev => ({
            ...prev,
            [actorId]: selectedRoles
        }));
    };

    // Email validation helper
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
    };

    // Add email to actor notification list
    const handleAddNotificationEmail = (actorId, email) => {
        const trimmedEmail = email.trim();

        if (!trimmedEmail) {
            return { success: false, error: t('emailEmpty', { ns: 'labels' }) || 'L\'email non può essere vuota' };
        }

        if (!isValidEmail(trimmedEmail)) {
            return { success: false, error: t('emailInvalid', { ns: 'labels' }) || 'Formato email non valido' };
        }

        const currentEmails = actorNotificationEmails[actorId] || [];

        if (currentEmails.includes(trimmedEmail)) {
            return { success: false, error: t('emailDuplicate', { ns: 'labels' }) || 'Questa email è già presente nella lista' };
        }

        setActorNotificationEmails(prev => ({
            ...prev,
            [actorId]: [...currentEmails, trimmedEmail]
        }));

        return { success: true };
    };

    // Remove email from actor notification list
    const handleRemoveNotificationEmail = (actorId, emailToRemove) => {
        setActorNotificationEmails(prev => ({
            ...prev,
            [actorId]: (prev[actorId] || []).filter(email => email !== emailToRemove)
        }));
    };

    // Determine if an actor should show notification options
    const shouldShowNotificationOptions = (actorId) => {
        if (!assignmentWithNotification) {
            return false;
        }

        // Get sorted actors by order
        const sortedActors = formActors
            .map(actorData => ({
                id: actorData.actor.id,
                order: actorOrder[actorData.actor.id] || 1,
                isAllUsers: actorAllUsers[actorData.actor.id] || false,
                hasRoles: (actorRoleMappings[actorData.actor.id] || []).length > 0
            }))
            .sort((a, b) => a.order - b.order);

        const currentActorIndex = sortedActors.findIndex(actor => actor.id === actorId);

        if (currentActorIndex === -1 || currentActorIndex === sortedActors.length - 1) {
            // Last actor in the sequence - can have notifications (for validators/supervisors)
            // or actor not found
            return currentActorIndex !== -1;
        }

        const currentActor = sortedActors[currentActorIndex];

        // If current actor has specific roles, it can have notifications
        if (!currentActor.isAllUsers && currentActor.hasRoles) {
            return true;
        }

        // If current actor is "All Users", check if it's the last "All Users" before role-based actors
        if (currentActor.isAllUsers) {
            // Find if there's any role-based actor after this one
            for (let i = currentActorIndex + 1; i < sortedActors.length; i++) {
                if (!sortedActors[i].isAllUsers && sortedActors[i].hasRoles) {
                    // This is the last "All Users" before role-based actors
                    return true;
                }
            }
            // There are only "All Users" actors after this one, no notification needed
            return false;
        }

        return false;
    };

    // Handle form assignment using new API
    const handleAssign = async () => {
        // Validate that at least one user or form is selected
        if (activeTab === 'user' && !selectedUserObject) {
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: t('userRequired', { ns: 'labels' }),
                icon: 'error'
            });
            return;
        }

        if (activeTab === 'group' && !selectedGroupObject) {
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: t('groupRequired', { ns: 'labels' }),
                icon: 'error'
            });
            return;
        }

        if (!selectedForm) {
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: t('formRequired', { ns: 'labels' }) || 'Seleziona un form',
                icon: 'error'
            });
            return;
        }

        // Validate actor-role mappings for collaborative assignments
        if (activeTab === 'collaborative') {
            // Check if there are actors
            if (formActors.length === 0) {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: t('noActorsFound', { ns: 'labels' }) || 'Nessun attore trovato per questo form',
                    icon: 'error'
                });
                return;
            }

            // Check if all actors have at least one role assigned (only if not free assignments)
            if (!freeAssignments) {
                const actorsWithoutRoles = formActors.filter(actorData => {
                    const roles = actorRoleMappings[actorData.actor.id] || [];
                    return roles.length === 0;
                });

                if (actorsWithoutRoles.length > 0) {
                    const actorNames = actorsWithoutRoles.map(a => a.actor.name).join(', ');
                    const errorMessage = `${t('actorsWithoutRoles', { ns: 'labels' }) || 'I seguenti attori non hanno ruoli assegnati'}: ${actorNames}`;
                    MySwal.fire({
                        title: t('error', { ns: 'labels' }),
                        text: errorMessage,
                        icon: 'error'
                    });
                    return;
                }
            }

            // Check if at least one business unit is selected (only if not free assignments)
            if (!freeAssignments && selectedBusinessUnits.length === 0) {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: t('businessUnitRequired', { ns: 'labels' }) || 'Seleziona almeno una business unit',
                    icon: 'error'
                });
                return;
            }

            // Validate order sequence (only if assignment with notification)
            if (assignmentWithNotification) {
                const orderValues = formActors.map(actorData => actorOrder[actorData.actor.id] || 1);
                const uniqueOrders = new Set(orderValues);

                // Check for duplicate order values
                if (uniqueOrders.size !== orderValues.length) {
                    MySwal.fire({
                        title: t('error', { ns: 'labels' }),
                        text: t('duplicateOrderValues', { ns: 'labels' }) || 'Ogni attore deve avere un ordine di compilazione univoco. Verifica che non ci siano duplicati.',
                        icon: 'error'
                    });
                    return;
                }

                // Validate "All Users" placement when both freeAssignments and assignmentWithNotification are active
                if (freeAssignments) {
                    // Create array of actors sorted by order
                    const sortedActors = formActors
                        .map(actorData => ({
                            id: actorData.actor.id,
                            name: actorData.actor.name,
                            order: actorOrder[actorData.actor.id] || 1,
                            isAllUsers: actorAllUsers[actorData.actor.id] || false,
                            hasRoles: (actorRoleMappings[actorData.actor.id] || []).length > 0
                        }))
                        .sort((a, b) => a.order - b.order);

                    // Check that at least one actor has specific roles (not all can be "All Users")
                    const hasAtLeastOneWithRoles = sortedActors.some(actor => !actor.isAllUsers && actor.hasRoles);
                    if (!hasAtLeastOneWithRoles) {
                        MySwal.fire({
                            title: t('error', { ns: 'labels' }),
                            text: t('atLeastOneActorWithRoles', { ns: 'labels' }) || 'Almeno un attore deve avere ruoli specifici assegnati. Non tutti gli attori possono essere impostati su "Tutti gli utenti".',
                            icon: 'error'
                        });
                        return;
                    }

                    // Check that "All Users" actors are only at the beginning of the sequence
                    let foundRoleBasedActor = false;
                    for (const actor of sortedActors) {
                        if (!actor.isAllUsers && actor.hasRoles) {
                            foundRoleBasedActor = true;
                        } else if (actor.isAllUsers && foundRoleBasedActor) {
                            MySwal.fire({
                                title: t('error', { ns: 'labels' }),
                                text: t('allUsersActorsMustBeFirst', { ns: 'labels' }) || `Gli attori con "Tutti gli utenti" devono essere posizionati all'inizio della sequenza di compilazione. L'attore "${actor.name}" (ordine ${actor.order}) non può seguire un attore con ruoli specifici.`,
                                icon: 'error'
                            });
                            return;
                        }
                    }
                }

                // Validate notification settings for actors with custom notifications
                for (const actorData of formActors) {
                    const actorId = actorData.actor.id;

                    // Only validate if actor should have notification options
                    if (shouldShowNotificationOptions(actorId)) {
                        const hasCustomNotification = actorNotificationCustom[actorId] || false;

                        // If custom notification is selected, check if emails are provided
                        if (hasCustomNotification) {
                            const emails = actorNotificationEmails[actorId] || [];

                            if (emails.length === 0) {
                                MySwal.fire({
                                    title: t('error', { ns: 'labels' }),
                                    text: `${t('customNotificationRequiresEmails', { ns: 'labels' }) || 'La notifica personalizzata richiede almeno un\'email'} (${t('actor', { ns: 'labels' }) || 'Attore'}: ${actorData.actor.name})`,
                                    icon: 'error'
                                });
                                return;
                            }
                        }
                    }
                }
            }
        }

        try {
            setSaving(true);

            // Show loading modal
            MySwal.fire({
                title: t('saving', { ns: 'labels' }),
                text: t('saving', { ns: 'labels' }),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Prepare assignment object according to new API documentation
            const assignment = {
                FormId: selectedForm,
                ToValidate: requiresValidation,
                Cycles: cycles || 1,
                Status: "Active"
            };

            // Set user or group (mutually exclusive), or neither for collaborative
            if (activeTab === 'user') {
                assignment.UserId = selectedUserObject.Id || selectedUserObject.id || selectedUserObject.ID;
            } else if (activeTab === 'group') {
                assignment.GroupId = selectedGroupObject.Id;
            } else if (activeTab === 'collaborative') {
                // For collaborative assignments, build complete actor-role mappings with full actor objects
                const collaborativeSchema = formActors.map(actorData => {
                    const schemaItem = {
                        actor: {
                            id: actorData.actor.id,
                            name: actorData.actor.name
                        },
                        roles: actorRoleMappings[actorData.actor.id] || [],
                        allUsers: actorAllUsers[actorData.actor.id] || false
                    };

                    // Add order and completed only if assignment with notification is enabled
                    if (assignmentWithNotification) {
                        schemaItem.order = actorOrder[actorData.actor.id] || 1;
                        schemaItem.completed = false;

                        // Add notification settings if actor qualifies for notifications
                        if (shouldShowNotificationOptions(actorData.actor.id)) {
                            const hasAutomatic = actorNotificationAutomatic[actorData.actor.id] || false;
                            const hasCustom = actorNotificationCustom[actorData.actor.id] || false;

                            // Only add notification data if at least one option is selected
                            if (hasAutomatic || hasCustom) {
                                schemaItem.notificationInfo = {
                                    automatic: hasAutomatic,
                                    custom: hasCustom
                                };

                                // Add custom emails if custom notification is enabled and emails exist
                                if (hasCustom) {
                                    const emails = actorNotificationEmails[actorData.actor.id] || [];
                                    if (emails.length > 0) {
                                        schemaItem.notificationInfo.customEmails = emails;
                                    }
                                }
                            }
                        }
                    }

                    return schemaItem;
                });
                assignment.AssignmentCollaborativeSchema = collaborativeSchema;

                // Add business units for collaborative assignments
                assignment.BusinessUnits = selectedBusinessUnits;

                // Add collaborative assignment flags
                assignment.FreeAssignments = freeAssignments;
                assignment.AssignmentWithNotification = assignmentWithNotification;
                //assignment.actualOrderCount = 1;
            }

            // Add dates if provided (convert to ISO format)
            if (expectedCompilationStartDate) {
                assignment.StartDate = new Date(expectedCompilationStartDate).toISOString();
            }
            if (expectedCompilationEndDate) {
                assignment.EndDate = new Date(expectedCompilationEndDate).toISOString();
            }

            // Call the new API endpoint
            const result = await api.sendMessage("darm/formAssignments/save", {
                user: currentUser(),
                body: { assignment }
            });

            if (result?.success) {
                // Show success message
                MySwal.fire({
                    title: t('success', { ns: 'labels' }),
                    text: t('assignSuccess', { ns: 'labels' }),
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                // Reset fields to allow for another assignment, keeping the form selected.
                setSelectedUser("");
                setSelectedUserObject(null);
                setSelectedGroup("");
                setSelectedGroupObject(null);
                setRequiresValidation(false);
                setExpectedCompilationStartDate("");
                setExpectedCompilationEndDate("");
                setCycles(1);

                // Reset collaborative assignment fields
                if (activeTab === 'collaborative') {
                    const initialMappings = {};
                    const initialAllUsers = {};
                    const initialOrder = {};
                    const initialNotificationAutomatic = {};
                    const initialNotificationCustom = {};
                    const initialNotificationEmails = {};
                    formActors.forEach((actorData, index) => {
                        initialMappings[actorData.actor.id] = [];
                        initialAllUsers[actorData.actor.id] = false;
                        initialOrder[actorData.actor.id] = index + 1;
                        initialNotificationAutomatic[actorData.actor.id] = false;
                        initialNotificationCustom[actorData.actor.id] = false;
                        initialNotificationEmails[actorData.actor.id] = [];
                    });
                    setActorRoleMappings(initialMappings);
                    setActorAllUsers(initialAllUsers);
                    setActorOrder(initialOrder);
                    setActorNotificationAutomatic(initialNotificationAutomatic);
                    setActorNotificationCustom(initialNotificationCustom);
                    setActorNotificationEmails(initialNotificationEmails);
                    setSelectedBusinessUnits([]);
                    setFreeAssignments(false);
                    setAssignmentWithNotification(false);
                }
            } else {
                throw new Error(result?.error || t('assignError', { ns: 'labels' }));
            }
        } catch (error) {
            console.error("Error assigning form:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('assignError', { ns: 'labels' }),
                icon: 'error'
            });
        } finally {
            setSaving(false);
        }
    };



    // Search assignments function
    const searchAssignments = async (filters = searchFilters) => {
        try {
            setSearchLoading(true);

            // Build request body with only non-empty filters
            const requestBody = {};
            
            if (filters.includeInactive) {
                requestBody.includeInactive = filters.includeInactive;
            }
            if (filters.userId) {
                requestBody.userId = filters.userId;
            }
            if (filters.groupId) {
                requestBody.groupId = filters.groupId;
            }
            if (filters.formId) {
                requestBody.formId = filters.formId;
            }
            if (filters.startDateFrom) {
                requestBody.startDateFrom = filters.startDateFrom;
            }
            if (filters.endDateBefore) {
                requestBody.endDateBefore = filters.endDateBefore;
            }
            if (filters.requiresValidation !== null) {
                requestBody.requiresValidation = filters.requiresValidation;
            }

            const result = await api.sendMessage("darm/formAssignments/getFiltered", {
                user: currentUser(),
                body: requestBody
            });

            if (result?.success) {
                const assignments = result.assignments || [];
                setAssignments(assignments);
                
                // Load user and group names for the assignments to populate cache
                if (assignments.length > 0) {
                    // Collect unique user IDs and group IDs from assignments
                    const userIds = [...new Set(assignments.filter(a => a.UserId).map(a => a.UserId))];
                    const groupIds = [...new Set(assignments.filter(a => a.GroupId).map(a => a.GroupId))];
                    
                    // Load users if any
                    if (userIds.length > 0) {
                        try {
                            const usersResult = await api.sendMessage("admin/security/getAllUsers", {
                                user: currentUser(),
                                body: { userIds: userIds }
                            });
                            
                            if (usersResult?.success && usersResult?.users) {
                                const formattedUsers = usersResult.users.map(user => {
                                    const name = user.Name || '';
                                    const surname = user.LastName || '';
                                    const email = user.EMail || '';
                                    
                                    let displayName = name;
                                    if (surname) {
                                        displayName += ` ${surname}`;
                                    }
                                    if (email) {
                                        displayName += ` (${email})`;
                                    }
                                    
                                    return {
                                        Code: user.Id,
                                        Name: displayName || email || user.Username || 'ANONYMOUS',
                                        OriginalUser: user
                                    };
                                });
                                // Update users state to include loaded users
                                setUsers(prev => {
                                    const merged = [...prev];
                                    formattedUsers.forEach(newUser => {
                                        if (!merged.find(u => u.Code === newUser.Code)) {
                                            merged.push(newUser);
                                        }
                                    });
                                    return merged;
                                });
                            }
                        } catch (error) {
                            console.error("Error loading user names for assignments:", error);
                        }
                    }
                    
                    // Load groups if any
                    if (groupIds.length > 0) {
                        try {
                            const groupsResult = await api.sendMessage("admin/security/getGroups", {
                                user: currentUser(),
                                body: { groupIds: groupIds }
                            });
                            
                            if (groupsResult?.success && groupsResult?.groups) {
                                const formattedGroups = groupsResult.groups.map(group => ({
                                    Code: group.Id,
                                    Name: group.Name || 'Unnamed Group',
                                    OriginalGroup: group
                                }));
                                // Update groups state to include loaded groups
                                setGroups(prev => {
                                    const merged = [...prev];
                                    formattedGroups.forEach(newGroup => {
                                        if (!merged.find(g => g.Code === newGroup.Code)) {
                                            merged.push(newGroup);
                                        }
                                    });
                                    return merged;
                                });
                            }
                        } catch (error) {
                            console.error("Error loading group names for assignments:", error);
                        }
                    }
                }
            } else {
                throw new Error(result?.error || t('searchError', { ns: 'labels' }));
            }
        } catch (error) {
            console.error("Error searching assignments:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('searchError', { ns: 'labels' }),
                icon: 'error'
            });
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle search filter changes
    const handleFilterChange = (filterName, value) => {
        const newFilters = { ...searchFilters, [filterName]: value };
        
        // Validazione date: se si imposta endDateBefore, deve essere dopo startDateFrom
        if (filterName === 'endDateBefore' && value && newFilters.startDateFrom) {
            const startDate = new Date(newFilters.startDateFrom);
            const endDate = new Date(value);
            
            if (endDate <= startDate) {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: t('endDateMustBeAfterStartDate', { ns: 'labels' }) || 'La data fine deve essere successiva alla data inizio',
                    icon: 'error'
                });
                return; // Non aggiorna il filtro se la validazione fallisce
            }
        }
        
        // Validazione date: se si imposta startDateFrom e esiste endDateBefore, controlla coerenza
        if (filterName === 'startDateFrom' && value && newFilters.endDateBefore) {
            const startDate = new Date(value);
            const endDate = new Date(newFilters.endDateBefore);
            
            if (endDate <= startDate) {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: t('startDateMustBeBeforeEndDate', { ns: 'labels' }) || 'La data inizio deve essere precedente alla data fine',
                    icon: 'error'
                });
                return; // Non aggiorna il filtro se la validazione fallisce
            }
        }
        
        setSearchFilters(newFilters);
    };

    // Handle clear filters
    const handleClearFilters = () => {
        const clearedFilters = {
            includeInactive: false,
            userId: "",
            groupId: "",
            formId: "",
            startDateFrom: "",
            endDateBefore: "",
            requiresValidation: null
        };
        setSearchFilters(clearedFilters);
        setAssignments([]);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('it-IT');
        } catch (error) {
            return dateString;
        }
    };

    // Get user name from ID (checks search results and filter results cache)
    const getUserName = (userId) => {
        if (!userId) return '-';
        
        // Check in user search results cache
        const userInSearchResults = userSearchResults.find(u => u.Code === userId);
        if (userInSearchResults) return userInSearchResults.Name;
        
        // Check in filter search results cache
        const userInFilterResults = filterUserSearchResults.find(u => u.Code === userId);
        if (userInFilterResults) return userInFilterResults.Name;
        
        // Check in users state (for backward compatibility)
        const user = users.find(u => u.Code === userId);
        if (user) return user.Name;
        
        // Return the userId as fallback
        return userId;
    };

    // Get group name from ID (checks search results and filter results cache)
    const getGroupName = (groupId) => {
        if (!groupId) return '-';
        
        // Check in group search results cache
        const groupInSearchResults = groupSearchResults.find(g => g.Code === groupId);
        if (groupInSearchResults) return groupInSearchResults.Name;
        
        // Check in filter search results cache
        const groupInFilterResults = filterGroupSearchResults.find(g => g.Code === groupId);
        if (groupInFilterResults) return groupInFilterResults.Name;
        
        // Check in groups state (for backward compatibility)
        const group = groups.find(g => g.Code === groupId);
        if (group) return group.Name;
        
        // Return the groupId as fallback
        return groupId;
    };

    // Get form name from ID
    const getFormName = (formId) => {
        if (!formId) return '-';
        const form = formList.find(f => f.Code === formId);
        return form ? form.Name : formId;
    };

    // Open edit modal with assignment data
    const openEditModal = (assignment) => {
        setEditingAssignment(assignment);
        
        // Find the user or group objects
        const userObj = assignment.UserId ? users.find(u => u.Code === assignment.UserId) : null;
        const groupObj = assignment.GroupId ? groups.find(g => g.Code === assignment.GroupId) : null;
        
        setEditForm({
            selectedUser: assignment.UserId || "",
            selectedGroup: assignment.GroupId || "",
            selectedForm: assignment.FormId || "",
            requiresValidation: assignment.ToValidate || false,
            expectedCompilationStartDate: assignment.StartDate ? assignment.StartDate.split('T')[0] : "",
            expectedCompilationEndDate: assignment.EndDate ? assignment.EndDate.split('T')[0] : "",
            cycles: assignment.Cycles || 1,
            activeTab: assignment.UserId ? 'user' : 'group'
        });
        
        setShowEditModal(true);
    };

    // Close edit modal
    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingAssignment(null);
        setEditForm({
            selectedUser: "",
            selectedGroup: "",
            selectedForm: "",
            requiresValidation: false,
            expectedCompilationStartDate: "",
            expectedCompilationEndDate: "",
            cycles: 1,
            activeTab: 'user'
        });
    };

    // Save assignment changes
    const handleSaveChanges = async () => {
        // Show confirmation dialog
        const confirmResult = await MySwal.fire({
            title: t('confirmSave', { ns: 'labels' }) || 'Conferma salvataggio',
            text: t('areYouSureToSave', { ns: 'labels' }) || 'Sei sicuro di voler salvare le modifiche?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('yes', { ns: 'labels' }) || 'Sì',
            cancelButtonText: t('cancel', { ns: 'labels' }) || 'Annulla'
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        try {
            setSaving(true);

            // Show loading modal
            MySwal.fire({
                title: t('saving', { ns: 'labels' }),
                text: t('saving', { ns: 'labels' }),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Prepare assignment object
            const assignment = {
                Id: editingAssignment.Id, // Include ID for update
                FormId: editForm.selectedForm,
                ToValidate: editForm.requiresValidation,
                Cycles: editForm.cycles || 1,
                Status: editingAssignment.Status // Keep original status
            };

            // Set user or group (mutually exclusive)
            if (editForm.activeTab === 'user') {
                assignment.UserId = editForm.selectedUser;
                assignment.GroupId = null; // Clear group when user is selected
            } else {
                assignment.GroupId = editForm.selectedGroup;
                assignment.UserId = null; // Clear user when group is selected
            }

            // Add dates if provided (convert to ISO format)
            if (editForm.expectedCompilationStartDate) {
                assignment.StartDate = new Date(editForm.expectedCompilationStartDate).toISOString();
            }
            if (editForm.expectedCompilationEndDate) {
                assignment.EndDate = new Date(editForm.expectedCompilationEndDate).toISOString();
            }

            // Call the save API endpoint
            const result = await api.sendMessage("darm/formAssignments/save", {
                user: currentUser(),
                body: { assignment }
            });

            if (result?.success) {
                // Show success message
                MySwal.fire({
                    title: t('success', { ns: 'labels' }),
                    text: t('updateSuccess', { ns: 'labels' }) || 'Assegnazione aggiornata con successo',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                // Close modal and refresh search results
                closeEditModal();
                searchAssignments();
            } else {
                throw new Error(result?.error || t('updateError', { ns: 'labels' }) || 'Errore durante l\'aggiornamento');
            }
        } catch (error) {
            console.error("Error updating assignment:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('updateError', { ns: 'labels' }) || 'Errore durante l\'aggiornamento',
                icon: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    // Delete assignment
    const handleDeleteAssignment = async () => {
        // Show confirmation dialog
        const confirmResult = await MySwal.fire({
            title: t('confirmDelete', { ns: 'labels' }) || 'Conferma eliminazione',
            text: t('areYouSureToDelete', { ns: 'labels' }) || 'Sei sicuro di voler eliminare questa assegnazione? Questa azione non può essere annullata.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('delete', { ns: 'labels' }) || 'Elimina',
            cancelButtonText: t('cancel', { ns: 'labels' }) || 'Annulla'
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        try {
            setSaving(true);

            // Show loading modal
            MySwal.fire({
                title: t('deleting', { ns: 'labels' }) || 'Eliminazione in corso...',
                text: t('deleting', { ns: 'labels' }) || 'Eliminazione in corso...',
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Call the delete API endpoint
            const result = await api.sendMessage("darm/formAssignments/delete", {
                user: currentUser(),
                body: { id: editingAssignment.Id }
            });

            if (result?.success) {
                // Show success message
                MySwal.fire({
                    title: t('success', { ns: 'labels' }),
                    text: t('deleteSuccess', { ns: 'labels' }) || 'Assegnazione eliminata con successo',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                // Close modal and refresh search results
                closeEditModal();
                searchAssignments();
            } else {
                throw new Error(result?.error || t('deleteError', { ns: 'labels' }) || 'Errore durante l\'eliminazione');
            }
        } catch (error) {
            console.error("Error deleting assignment:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('deleteError', { ns: 'labels' }) || 'Errore durante l\'eliminazione',
                icon: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb"></ol>

            </div>

            <div className="container-fluid">
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="heading mb-0">
                                    {t('formAssignment', { ns: 'labels' })}
                                </h4>
                            </div>

                            <div className="card-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                                {loading ? (
                                    <div className="d-flex justify-content-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Main Tabs: Assignment / Search */}
                                        <ul className="nav nav-tabs nav-tabs-primary mb-4" role="tablist">
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link ${mainTab === 'assignment' ? 'active' : ''}`}
                                                    onClick={() => setMainTab('assignment')}
                                                    type="button"
                                                    role="tab"
                                                >
                                                    <i className="fa-solid fa-user-plus me-2"></i>
                                                    {t('newAssignment', { ns: 'labels' }) || 'Nuova Assegnazione'}
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link ${mainTab === 'search' ? 'active' : ''}`}
                                                    onClick={() => setMainTab('search')}
                                                    type="button"
                                                    role="tab"
                                                >
                                                    <i className="fa-solid fa-search me-2"></i>
                                                    {t('searchAssignments', { ns: 'labels' }) || 'Ricerca Assegnazioni'}
                                                </button>
                                            </li>
                                        </ul>

                                        {/* Assignment Tab Content */}
                                        {mainTab === 'assignment' && (
                                            <>
                                                <div className="alert alert-info">
                                                    <i className="fa-solid fa-info-circle me-2"></i>
                                                    {t('formAssignmentInfo', { ns: 'labels' })}
                                                </div>

                                                <ul className="nav nav-tabs" id="assign-tab" role="tablist">
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link ${activeTab === 'user' ? 'active' : ''}`} id="user-tab" onClick={() => {
                                                    setActiveTab('user');
                                                    // Reset form selection if switching from collaborative tab
                                                    if (activeTab === 'collaborative') {
                                                        setSelectedForm('');
                                                    }
                                                }} type="button" role="tab" aria-controls="user-tab-pane" aria-selected={activeTab === 'user'}>
                                                    {t('assignToUser', { ns: 'labels' })}
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link ${activeTab === 'group' ? 'active' : ''}`} id="group-tab" onClick={() => {
                                                    setActiveTab('group');
                                                    // Reset form selection if switching from collaborative tab
                                                    if (activeTab === 'collaborative') {
                                                        setSelectedForm('');
                                                    }
                                                }} type="button" role="tab" aria-controls="group-tab-pane" aria-selected={activeTab === 'group'}>
                                                    {t('assignToGroup', { ns: 'labels' })}
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link ${activeTab === 'collaborative' ? 'active' : ''}`} id="collaborative-tab" onClick={() => {
                                                    setActiveTab('collaborative');
                                                    // Reset form selection if current form is not collaborative
                                                    if (selectedForm) {
                                                        const currentForm = formList.find(f => f.Code === selectedForm);
                                                        if (!currentForm?.IsCollaborative) {
                                                            setSelectedForm('');
                                                        }
                                                    }
                                                }} type="button" role="tab" aria-controls="collaborative-tab-pane" aria-selected={activeTab === 'collaborative'}>
                                                    {t('collaborativeAssignment', { ns: 'labels' }) || 'Assegnazione Collaborativa'}
                                                </button>
                                            </li>
                                        </ul>

                                        <div className="tab-content pt-3" id="assign-tab-content">
                                            <div className={`tab-pane fade ${activeTab === 'user' ? 'show active' : ''}`} id="user-tab-pane" role="tabpanel" aria-labelledby="user-tab">
                                                <label className="form-label">
                                                    {t('selectUser', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                
                                                {/* Selected user display */}
                                                {selectedUserObject && (
                                                    <div className="alert alert-info d-flex justify-content-between align-items-center mb-2">
                                                        <div>
                                                            <i className="fa-solid fa-user me-2"></i>
                                                            <strong>
                                                                {selectedUserObject.Name || ''} {selectedUserObject.LastName || ''}
                                                            </strong>
                                                            {selectedUserObject.EMail && (
                                                                <span className="ms-2 text-muted">({selectedUserObject.EMail})</span>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => {
                                                                setSelectedUser('');
                                                                setSelectedUserObject(null);
                                                            }}
                                                            title={t('remove', { ns: 'labels' }) || 'Rimuovi'}
                                                        >
                                                            <i className="fa-solid fa-times"></i>
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                {/* Search input */}
                                                {!selectedUserObject && (
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder={t('searchUserPlaceholder', { ns: 'labels' }) || 'Digita almeno 3 caratteri per cercare...'}
                                                            value={userSearchTerm}
                                                            onChange={(e) => handleUserSearchChange(e.target.value)}
                                                            onFocus={() => {
                                                                if (userSearchResults.length > 0) {
                                                                    setShowUserSearchResults(true);
                                                                }
                                                            }}
                                                        />
                                                        {isSearchingUsers && (
                                                            <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Search results dropdown */}
                                                        {showUserSearchResults && (
                                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ maxHeight: '300px', overflowY: 'auto', zIndex: 1000 }}>
                                                                {userSearchResults.length > 0 ? (
                                                                    <div className="list-group list-group-flush">
                                                                        {userSearchResults.map(user => (
                                                                            <button
                                                                                key={user.Code}
                                                                                type="button"
                                                                                className="list-group-item list-group-item-action text-start"
                                                                                onClick={() => handleSelectUserFromSearch(user)}
                                                                            >
                                                                                <i className="fa-solid fa-user me-2"></i>
                                                                                {user.Name}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div className="p-3 text-center text-muted">
                                                                        <i className="fa-solid fa-info-circle me-2"></i>
                                                                        {t('noUsersFound', { ns: 'labels' }) || 'Nessun utente trovato'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className={`tab-pane fade ${activeTab === 'group' ? 'show active' : ''}`} id="group-tab-pane" role="tabpanel" aria-labelledby="group-tab">
                                                <label className="form-label">
                                                    {t('selectGroup', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                
                                                {/* Selected group display */}
                                                {selectedGroupObject && (
                                                    <div className="alert alert-info d-flex justify-content-between align-items-center mb-2">
                                                        <div>
                                                            <i className="fa-solid fa-users me-2"></i>
                                                            <strong>{selectedGroupObject.Name || 'Unnamed Group'}</strong>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => {
                                                                setSelectedGroup('');
                                                                setSelectedGroupObject(null);
                                                            }}
                                                            title={t('remove', { ns: 'labels' }) || 'Rimuovi'}
                                                        >
                                                            <i className="fa-solid fa-times"></i>
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                {/* Search input */}
                                                {!selectedGroupObject && (
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder={t('searchGroupPlaceholder', { ns: 'labels' }) || 'Digita almeno 2 caratteri per cercare...'}
                                                            value={groupSearchTerm}
                                                            onChange={(e) => handleGroupSearchChange(e.target.value)}
                                                            onFocus={() => {
                                                                if (groupSearchResults.length > 0) {
                                                                    setShowGroupSearchResults(true);
                                                                }
                                                            }}
                                                        />
                                                        {isSearchingGroups && (
                                                            <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Search results dropdown */}
                                                        {showGroupSearchResults && (
                                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ maxHeight: '300px', overflowY: 'auto', zIndex: 1000 }}>
                                                                {groupSearchResults.length > 0 ? (
                                                                    <div className="list-group list-group-flush">
                                                                        {groupSearchResults.map(group => (
                                                                            <button
                                                                                key={group.Code}
                                                                                type="button"
                                                                                className="list-group-item list-group-item-action text-start"
                                                                                onClick={() => handleSelectGroupFromSearch(group)}
                                                                            >
                                                                                <i className="fa-solid fa-users me-2"></i>
                                                                                {group.Name}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div className="p-3 text-center text-muted">
                                                                        <i className="fa-solid fa-info-circle me-2"></i>
                                                                        {t('noGroupsFound', { ns: 'labels' }) || 'Nessun gruppo trovato'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className={`tab-pane fade ${activeTab === 'collaborative' ? 'show active' : ''}`} id="collaborative-tab-pane" role="tabpanel" aria-labelledby="collaborative-tab">
                                                <div className="alert alert-info">
                                                    <i className="fa-solid fa-info-circle me-2"></i>
                                                    {t('collaborativeAssignmentInfo', { ns: 'labels' }) || 'Assegnazione collaborativa: seleziona un form collaborativo per creare un\'assegnazione aperta a tutti gli utenti.'}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row g-3 mt-2">
                                            <div className="col-md-12">
                                                <label className="form-label">
                                                    {t('selectForm', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={activeTab === 'collaborative' ? collaborativeForms : formList}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={selectedForm ? [{ Code: selectedForm }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        setSelectedForm(selected);
                                                    }}
                                                    placeholder={t('selectForm', { ns: 'labels' })}
                                                    dropDownParent="root"
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('cycles', { ns: 'labels' }) || 'Cicli'}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={cycles}
                                                    onChange={(e) => setCycles(parseInt(e.target.value) || 1)}
                                                    min="1"
                                                    max="100"
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('expectedStartDate', { ns: 'labels' })}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={expectedCompilationStartDate}
                                                    onChange={(e) => setExpectedCompilationStartDate(e.target.value)}
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('expectedEndDate', { ns: 'labels' })}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={expectedCompilationEndDate}
                                                    onChange={(e) => setExpectedCompilationEndDate(e.target.value)}
                                                />
                                            </div>

                                            <div className="col-12">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="requiresValidation"
                                                        checked={requiresValidation}
                                                        onChange={(e) => setRequiresValidation(e.target.checked)}
                                                    />
                                                    <label className="form-check-label" htmlFor="requiresValidation">
                                                        {t('requiresValidation', { ns: 'labels' })}
                                                    </label>
                                                </div>
                                            </div>

                                            {/* Assignment Type Flags - Only shown for collaborative assignments */}
                                            {activeTab === 'collaborative' && selectedForm && (
                                                <div className="col-12 mt-4">
                                                    <h6 className="mb-3">
                                                        {t('assignmentType', { ns: 'labels' }) || 'Tipo di Assegnazione'}
                                                    </h6>
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <div className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id="freeAssignments"
                                                                    checked={freeAssignments}
                                                                    onChange={(e) => setFreeAssignments(e.target.checked)}
                                                                />
                                                                <label className="form-check-label" htmlFor="freeAssignments">
                                                                    {t('freeAssignments', { ns: 'labels' }) || 'Assegnazioni libere'}
                                                                </label>
                                                            </div>
                                                            <small className="text-muted ms-4">
                                                                {t('freeAssignmentsInfo', { ns: 'labels' }) || 'Permette di assegnare attori a tutti gli utenti'}
                                                            </small>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id="assignmentWithNotification"
                                                                    checked={assignmentWithNotification}
                                                                    onChange={(e) => setAssignmentWithNotification(e.target.checked)}
                                                                />
                                                                <label className="form-check-label" htmlFor="assignmentWithNotification">
                                                                    {t('assignmentWithNotification', { ns: 'labels' }) || 'Assegnazione con notifica'}
                                                                </label>
                                                            </div>
                                                            <small className="text-muted ms-4">
                                                                {t('assignmentWithNotificationInfo', { ns: 'labels' }) || 'Invia notifica agli utenti in base all\'ordine di compilazione'}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Info message when both options are active */}
                                                    {freeAssignments && assignmentWithNotification && (
                                                        <div className="alert alert-warning mt-3">
                                                            <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                                            <strong>{t('importantNote', { ns: 'labels' }) || 'Nota importante'}:</strong>
                                                            <br />
                                                            {t('allUsersOrderRestriction', { ns: 'labels' }) || 'Gli attori con "Tutti gli utenti" devono essere posizionati all\'inizio della sequenza di compilazione. Dopo un attore con ruoli specifici, non è più possibile inserire attori con "Tutti gli utenti". Almeno un attore deve avere ruoli specifici.'}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Business Units - Only shown for collaborative assignments and when not free assignments */}
                                            {activeTab === 'collaborative' && selectedForm && !freeAssignments && (
                                                <div className="col-12 mt-4">
                                                    <h6 className="mb-3">
                                                        {t('selectBusinessUnits', { ns: 'labels' }) || 'Seleziona Business Unit'}
                                                        <span className="text-danger ms-1">*</span>
                                                    </h6>
                                                    <div className="alert alert-info mb-3">
                                                        <i className="fa-solid fa-info-circle me-2"></i>
                                                        {t('businessUnitsInfo', { ns: 'labels' }) || 'Seleziona una o più business unit per questa assegnazione collaborativa.'}
                                                    </div>

                                                    {/* Dropdown per selezionare business units */}
                                                    <div className="mb-3">
                                                        <label className="form-label">{t('selectBusinessUnitToAdd', { ns: 'labels' }) || 'Seleziona una business unit da aggiungere'}</label>
                                                        <Choices
                                                            voc={businessUnits.filter(bu => !selectedBusinessUnits.includes(bu.Code))}
                                                            value="Code"
                                                            text="Name"
                                                            className="inputStyle blackText bg-white"
                                                            selected={[]}
                                                            onChange={(e) => {
                                                                const selected = e.selected?.[0]?.Code;
                                                                if (selected && !selectedBusinessUnits.includes(selected)) {
                                                                    setSelectedBusinessUnits([...selectedBusinessUnits, selected]);
                                                                }
                                                            }}
                                                            placeholder={t('selectBusinessUnitToAdd', { ns: 'labels' }) || 'Seleziona una business unit'}
                                                            dropDownParent="root"
                                                            multiple={false}
                                                            allowClear={true}
                                                        />
                                                    </div>

                                                    {/* Lista business units selezionate */}
                                                    <div>
                                                        <label className="form-label">{t('selectedBusinessUnits', { ns: 'labels' }) || 'Business Unit selezionate'}</label>
                                                        {selectedBusinessUnits.length > 0 ? (
                                                            <div className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                                {selectedBusinessUnits.map(buCode => {
                                                                    const bu = businessUnits.find(b => b.Code === buCode);
                                                                    return bu ? (
                                                                        <div key={buCode} className="list-group-item px-2 py-2 d-flex justify-content-between align-items-center bg-white">
                                                                            <div className="d-flex align-items-center flex-grow-1 min-w-0">
                                                                                <i className="fas fa-building text-primary me-2 flex-shrink-0"></i>
                                                                                <span className="text-truncate" title={bu.Name}>{bu.Name}</span>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger py-0 px-2 ms-2 flex-shrink-0"
                                                                                onClick={() => {
                                                                                    setSelectedBusinessUnits(selectedBusinessUnits.filter(code => code !== buCode));
                                                                                }}
                                                                                title={t('remove', { ns: 'labels' }) || 'Rimuovi'}
                                                                            >
                                                                                <i className="fas fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div className="alert alert-warning mb-0 py-2">
                                                                <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                                                <span className="small">{t('noBusinessUnitsSelected', { ns: 'labels' }) || 'Nessuna business unit selezionata'}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Actor-Role Mappings - Only shown for collaborative assignments */}
                                            {activeTab === 'collaborative' && formActors.length > 0 && (
                                                <div className="col-12 mt-4">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h6 className="mb-0">
                                                            {t('actorRoleMapping', { ns: 'labels' }) || 'Assegnazione Attori ai Ruoli'}
                                                            <span className="text-danger ms-1">*</span>
                                                        </h6>

                                                        {/* Reset order button - Only shown when assignment with notification is active */}
                                                        {assignmentWithNotification && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => {
                                                                    const resetOrder = {};
                                                                    formActors.forEach((actorData, index) => {
                                                                        resetOrder[actorData.actor.id] = index + 1;
                                                                    });
                                                                    setActorOrder(resetOrder);
                                                                }}
                                                                title={t('resetOrder', { ns: 'labels' }) || 'Reimposta ordine sequenziale'}
                                                            >
                                                                <i className="fa-solid fa-arrow-rotate-left me-1"></i>
                                                                {t('resetOrder', { ns: 'labels' }) || 'Reimposta ordine'}
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="alert alert-warning">
                                                        <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                                        {t('actorRoleMappingRequired', { ns: 'labels' }) || 'Tutti gli attori devono essere assegnati ad almeno un ruolo per procedere.'}
                                                    </div>

                                                    {/* Griglia responsive per gli attori */}
                                                    <div className="row g-3" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                                        {formActors.map((actorData) => {
                                                            const selectedRoleCodes = actorRoleMappings[actorData.actor.id] || [];
                                                            const availableRoles = organizationalRoles.filter(role =>
                                                                !selectedRoleCodes.includes(role.Code)
                                                            );

                                                            return (
                                                                <div key={actorData.actor.id} className="col-12 col-md-6 col-xl-4">
                                                                    <div className="p-3 border rounded h-100 d-flex flex-column">
                                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                                            <label className="form-label fw-bold mb-0">
                                                                                <i className="fa-solid fa-user-tag me-2"></i>
                                                                                {actorData.actor.name}
                                                                                <span className="text-danger ms-1">*</span>
                                                                            </label>

                                                                            {/* Campo ordine - Solo se "Assegnazione con notifica" è attivo */}
                                                                            {assignmentWithNotification && (
                                                                                <div style={{ width: '80px' }}>
                                                                                    <select
                                                                                        className="form-select form-select-sm text-center"
                                                                                        value={actorOrder[actorData.actor.id] || 1}
                                                                                        onChange={(e) => {
                                                                                            const value = parseInt(e.target.value);
                                                                                            setActorOrder(prev => ({
                                                                                                ...prev,
                                                                                                [actorData.actor.id]: value
                                                                                            }));
                                                                                        }}
                                                                                        title={t('compilationOrder', { ns: 'labels' }) || 'Ordine di compilazione'}
                                                                                    >
                                                                                        {Array.from({ length: formActors.length }, (_, i) => i + 1).map(num => (
                                                                                            <option key={num} value={num}>
                                                                                                {num}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                    <small className="text-muted d-block text-center" style={{ fontSize: '0.7rem' }}>
                                                                                        {t('order', { ns: 'labels' }) || 'Ordine'}
                                                                                    </small>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Flag "Tutti gli utenti" - Solo se "Assegnazioni libere" è attivo */}
                                                                        {freeAssignments && (
                                                                            <div className="mb-3">
                                                                                <div className="form-check">
                                                                                    <input
                                                                                        className="form-check-input"
                                                                                        type="checkbox"
                                                                                        id={`allUsers_${actorData.actor.id}`}
                                                                                        checked={actorAllUsers[actorData.actor.id] || false}
                                                                                        onChange={(e) => {
                                                                                            const isChecked = e.target.checked;

                                                                                            // Update "Tutti gli utenti" flag
                                                                                            setActorAllUsers(prev => ({
                                                                                                ...prev,
                                                                                                [actorData.actor.id]: isChecked
                                                                                            }));

                                                                                            // If checked, clear all roles for this actor
                                                                                            if (isChecked) {
                                                                                                handleActorRoleChange(actorData.actor.id, []);
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                    <label className="form-check-label small" htmlFor={`allUsers_${actorData.actor.id}`}>
                                                                                        <i className="fa-solid fa-users me-1"></i>
                                                                                        {t('allUsers', { ns: 'labels' }) || 'Tutti gli utenti'}
                                                                                    </label>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {/* Dropdown per selezionare ruoli - Disabilitato se "Tutti gli utenti" è selezionato */}
                                                                        <div className="mb-3">
                                                                            <label className="form-label small">{t('selectRoleToAdd', { ns: 'labels' }) || 'Seleziona un ruolo da aggiungere'}</label>
                                                                            <select
                                                                                className="form-select form-select-sm"
                                                                                value=""
                                                                                disabled={freeAssignments && actorAllUsers[actorData.actor.id]}
                                                                                onChange={(e) => {
                                                                                    if (e.target.value) {
                                                                                        const newRoles = [...selectedRoleCodes, e.target.value];
                                                                                        handleActorRoleChange(actorData.actor.id, newRoles);
                                                                                        e.target.value = ""; // Reset selection
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <option value="">{t('selectRoleToAdd', { ns: 'labels' }) || 'Seleziona un ruolo'}</option>
                                                                                {availableRoles.map(role => (
                                                                                    <option key={role.Code} value={role.Code}>
                                                                                        {role.Name}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                            {freeAssignments && actorAllUsers[actorData.actor.id] && (
                                                                                <small className="text-muted d-block mt-1">
                                                                                    <i className="fa-solid fa-info-circle me-1"></i>
                                                                                    {t('rolesDisabledForAllUsers', { ns: 'labels' }) || 'La selezione dei ruoli è disabilitata quando "Tutti gli utenti" è attivo'}
                                                                                </small>
                                                                            )}
                                                                        </div>

                                                                        {/* Lista ruoli selezionati */}
                                                                        <div className="flex-grow-1">
                                                                            <label className="form-label small">{t('assignedRoles', { ns: 'labels' }) || 'Ruoli assegnati'}</label>
                                                                            {selectedRoleCodes.length > 0 ? (
                                                                                <div className="list-group list-group-flush" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                                                    {selectedRoleCodes.map(roleCode => {
                                                                                        const role = organizationalRoles.find(r => r.Code === roleCode);
                                                                                        const isAllUsersActive = freeAssignments && actorAllUsers[actorData.actor.id];
                                                                                        return role ? (
                                                                                            <div key={roleCode} className="list-group-item px-2 py-2 d-flex justify-content-between align-items-center bg-white">
                                                                                                <div className="d-flex align-items-center flex-grow-1 min-w-0">
                                                                                                    <i className="fas fa-user-shield text-primary me-2 flex-shrink-0"></i>
                                                                                                    <span className={`small text-truncate ${isAllUsersActive ? 'text-muted' : ''}`} title={role.Name}>{role.Name}</span>
                                                                                                </div>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="btn btn-sm btn-outline-danger py-0 px-2 ms-2 flex-shrink-0"
                                                                                                    onClick={() => {
                                                                                                        const newRoles = selectedRoleCodes.filter(code => code !== roleCode);
                                                                                                        handleActorRoleChange(actorData.actor.id, newRoles);
                                                                                                    }}
                                                                                                    disabled={isAllUsersActive}
                                                                                                    title={t('remove', { ns: 'labels' }) || 'Rimuovi'}
                                                                                                >
                                                                                                    <i className="fas fa-times"></i>
                                                                                                </button>
                                                                                            </div>
                                                                                        ) : null;
                                                                                    })}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="alert alert-info mb-0 py-2">
                                                                                    <p className="mb-0 small">{t('noRolesAssigned', { ns: 'labels' }) || 'Nessun ruolo assegnato'}</p>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Notification Settings - Only shown when assignmentWithNotification is active and actor qualifies */}
                                                                        {shouldShowNotificationOptions(actorData.actor.id) && (
                                                                            <div className="mt-3 pt-3 border-top">
                                                                                <label className="form-label small fw-bold">
                                                                                    <i className="fa-solid fa-bell me-1"></i>
                                                                                    {t('notificationSettings', { ns: 'labels' }) || 'Impostazioni Notifica'}
                                                                                </label>

                                                                                {/* Notification Type Selection */}
                                                                                <div className="mb-2">
                                                                                    <div className="form-check">
                                                                                        <input
                                                                                            className="form-check-input"
                                                                                            type="checkbox"
                                                                                            id={`notificationAutomatic_${actorData.actor.id}`}
                                                                                            checked={actorNotificationAutomatic[actorData.actor.id] || false}
                                                                                            onChange={(e) => {
                                                                                                setActorNotificationAutomatic(prev => ({
                                                                                                    ...prev,
                                                                                                    [actorData.actor.id]: e.target.checked
                                                                                                }));
                                                                                            }}
                                                                                        />
                                                                                        <label className="form-check-label small" htmlFor={`notificationAutomatic_${actorData.actor.id}`}>
                                                                                            <i className="fa-solid fa-cog me-1"></i>
                                                                                            {t('automaticNotification', { ns: 'labels' }) || 'Automatica'}
                                                                                        </label>
                                                                                    </div>
                                                                                    <div className="form-check">
                                                                                        <input
                                                                                            className="form-check-input"
                                                                                            type="checkbox"
                                                                                            id={`notificationCustom_${actorData.actor.id}`}
                                                                                            checked={actorNotificationCustom[actorData.actor.id] || false}
                                                                                            onChange={(e) => {
                                                                                                setActorNotificationCustom(prev => ({
                                                                                                    ...prev,
                                                                                                    [actorData.actor.id]: e.target.checked
                                                                                                }));
                                                                                            }}
                                                                                        />
                                                                                        <label className="form-check-label small" htmlFor={`notificationCustom_${actorData.actor.id}`}>
                                                                                            <i className="fa-solid fa-envelope me-1"></i>
                                                                                            {t('customNotification', { ns: 'labels' }) || 'Personalizzata'}
                                                                                        </label>
                                                                                    </div>
                                                                                </div>

                                                                                {/* Custom Email Management - Only shown when custom is selected */}
                                                                                {actorNotificationCustom[actorData.actor.id] && (
                                                                                    <div className="mt-2">
                                                                                        <label className="form-label small">{t('notificationEmails', { ns: 'labels' }) || 'Email per notifiche'}</label>

                                                                                        {/* Email input */}
                                                                                        <div className="input-group input-group-sm mb-2">
                                                                                            <input
                                                                                                type="email"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder={t('enterEmail', { ns: 'labels' }) || 'Inserisci email...'}
                                                                                                id={`emailInput_${actorData.actor.id}`}
                                                                                                onKeyDown={(e) => {
                                                                                                    if (e.key === 'Enter') {
                                                                                                        e.preventDefault();
                                                                                                        const input = e.target;
                                                                                                        const result = handleAddNotificationEmail(actorData.actor.id, input.value);
                                                                                                        if (result.success) {
                                                                                                            input.value = '';
                                                                                                        } else {
                                                                                                            MySwal.fire({
                                                                                                                title: t('error', { ns: 'labels' }),
                                                                                                                text: result.error,
                                                                                                                icon: 'error',
                                                                                                                timer: 3000
                                                                                                            });
                                                                                                        }
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                            <button
                                                                                                className="btn btn-outline-primary btn-sm"
                                                                                                type="button"
                                                                                                onClick={() => {
                                                                                                    const input = document.getElementById(`emailInput_${actorData.actor.id}`);
                                                                                                    const result = handleAddNotificationEmail(actorData.actor.id, input.value);
                                                                                                    if (result.success) {
                                                                                                        input.value = '';
                                                                                                    } else {
                                                                                                        MySwal.fire({
                                                                                                            title: t('error', { ns: 'labels' }),
                                                                                                            text: result.error,
                                                                                                            icon: 'error',
                                                                                                            timer: 3000
                                                                                                        });
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                <i className="fa-solid fa-plus"></i>
                                                                                            </button>
                                                                                        </div>

                                                                                        {/* Email list */}
                                                                                        {(actorNotificationEmails[actorData.actor.id] || []).length > 0 ? (
                                                                                            <div className="list-group list-group-flush" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                                                                {(actorNotificationEmails[actorData.actor.id] || []).map((email, index) => (
                                                                                                    <div key={index} className="list-group-item px-2 py-1 d-flex justify-content-between align-items-center bg-white">
                                                                                                        <div className="d-flex align-items-center flex-grow-1 min-w-0">
                                                                                                            <i className="fa-solid fa-envelope text-secondary me-2 flex-shrink-0" style={{ fontSize: '0.8rem' }}></i>
                                                                                                            <span className="small text-truncate" title={email}>{email}</span>
                                                                                                        </div>
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="btn btn-sm btn-outline-danger py-0 px-1 ms-2 flex-shrink-0"
                                                                                                            onClick={() => handleRemoveNotificationEmail(actorData.actor.id, email)}
                                                                                                            title={t('remove', { ns: 'labels' }) || 'Rimuovi'}
                                                                                                            style={{ fontSize: '0.7rem' }}
                                                                                                        >
                                                                                                            <i className="fas fa-times"></i>
                                                                                                        </button>
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        ) : (
                                                                                            <div className="alert alert-warning mb-0 py-1">
                                                                                                <small className="mb-0">{t('noEmailsAdded', { ns: 'labels' }) || 'Nessuna email aggiunta'}</small>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="col-12 mt-4">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleAssign}
                                                    disabled={saving}
                                                >
                                                    {saving ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            {t('saving', { ns: 'labels' })}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa-solid fa-user-plus me-2"></i>
                                                            {t('assign', { ns: 'labels' })}
                                                        </>
                                                    )}
                                                </button>

                                            </div>
                                        </div>
                                            </>
                                        )}

                                        {/* Search Tab Content */}
                                        {mainTab === 'search' && (
                                            <>
                                                <div className="alert alert-info">
                                                    <i className="fa-solid fa-info-circle me-2"></i>
                                                    {t('searchAssignmentsInfo', { ns: 'labels' }) || 'Cerca e gestisci le assegnazioni esistenti utilizzando i filtri sottostanti.'}
                                                </div>

                                {/* Filtri di ricerca */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('selectUser', { ns: 'labels' })}
                                        </label>
                                        
                                        {/* Selected user display for filters */}
                                        {searchFilters.userId && (
                                            <div className="alert alert-info d-flex justify-content-between align-items-center mb-2 py-2">
                                                <div>
                                                    <i className="fa-solid fa-user me-2"></i>
                                                    <strong>
                                                        {(() => {
                                                            const user = users.find(u => u.Code === searchFilters.userId);
                                                            return user ? user.Name : searchFilters.userId;
                                                        })()}
                                                    </strong>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger py-0 px-2"
                                                    onClick={() => {
                                                        handleFilterChange('userId', '');
                                                        setFilterUserSearchTerm('');
                                                    }}
                                                    title={t('remove', { ns: 'labels' }) || 'Rimuovi'}
                                                >
                                                    <i className="fa-solid fa-times"></i>
                                                </button>
                                            </div>
                                        )}
                                        
                                        {/* Search input for filters */}
                                        {!searchFilters.userId && (
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t('searchUserPlaceholder', { ns: 'labels' }) || 'Digita almeno 3 caratteri per cercare...'}
                                                    value={filterUserSearchTerm}
                                                    onChange={(e) => handleFilterUserSearchChange(e.target.value)}
                                                    onFocus={() => {
                                                        if (filterUserSearchResults.length > 0) {
                                                            setShowFilterUserSearchResults(true);
                                                        }
                                                    }}
                                                />
                                                {isSearchingFilterUsers && (
                                                    <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                    </div>
                                                )}
                                                
                                                {/* Search results dropdown */}
                                                {showFilterUserSearchResults && (
                                                    <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ maxHeight: '300px', overflowY: 'auto', zIndex: 1000 }}>
                                                        {filterUserSearchResults.length > 0 ? (
                                                            <div className="list-group list-group-flush">
                                                                {filterUserSearchResults.map(user => (
                                                                    <button
                                                                        key={user.Code}
                                                                        type="button"
                                                                        className="list-group-item list-group-item-action text-start"
                                                                        onClick={() => handleSelectFilterUserFromSearch(user.Code)}
                                                                    >
                                                                        <i className="fa-solid fa-user me-2"></i>
                                                                        {user.Name}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="p-3 text-center text-muted">
                                                                <i className="fa-solid fa-info-circle me-2"></i>
                                                                {t('noUsersFound', { ns: 'labels' }) || 'Nessun utente trovato'}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('selectGroup', { ns: 'labels' })}
                                        </label>
                                        
                                        {/* Selected group display for filters */}
                                        {searchFilters.groupId && (
                                            <div className="alert alert-info d-flex justify-content-between align-items-center mb-2 py-2">
                                                <div>
                                                    <i className="fa-solid fa-users me-2"></i>
                                                    <strong>
                                                        {(() => {
                                                            const group = groups.find(g => g.Code === searchFilters.groupId);
                                                            return group ? group.Name : searchFilters.groupId;
                                                        })()}
                                                    </strong>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger py-0 px-2"
                                                    onClick={() => {
                                                        handleFilterChange('groupId', '');
                                                        setFilterGroupSearchTerm('');
                                                    }}
                                                    title={t('remove', { ns: 'labels' }) || 'Rimuovi'}
                                                >
                                                    <i className="fa-solid fa-times"></i>
                                                </button>
                                            </div>
                                        )}
                                        
                                        {/* Search input for filters */}
                                        {!searchFilters.groupId && (
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t('searchGroupPlaceholder', { ns: 'labels' }) || 'Digita almeno 2 caratteri per cercare...'}
                                                    value={filterGroupSearchTerm}
                                                    onChange={(e) => handleFilterGroupSearchChange(e.target.value)}
                                                    onFocus={() => {
                                                        if (filterGroupSearchResults.length > 0) {
                                                            setShowFilterGroupSearchResults(true);
                                                        }
                                                    }}
                                                />
                                                {isSearchingFilterGroups && (
                                                    <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                    </div>
                                                )}
                                                
                                                {/* Search results dropdown */}
                                                {showFilterGroupSearchResults && (
                                                    <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ maxHeight: '300px', overflowY: 'auto', zIndex: 1000 }}>
                                                        {filterGroupSearchResults.length > 0 ? (
                                                            <div className="list-group list-group-flush">
                                                                {filterGroupSearchResults.map(group => (
                                                                    <button
                                                                        key={group.Code}
                                                                        type="button"
                                                                        className="list-group-item list-group-item-action text-start"
                                                                        onClick={() => handleSelectFilterGroupFromSearch(group.Code)}
                                                                    >
                                                                        <i className="fa-solid fa-users me-2"></i>
                                                                        {group.Name}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="p-3 text-center text-muted">
                                                                <i className="fa-solid fa-info-circle me-2"></i>
                                                                {t('noGroupsFound', { ns: 'labels' }) || 'Nessun gruppo trovato'}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('selectForm', { ns: 'labels' })}
                                        </label>
                                        <Choices
                                            voc={[{ Code: "", Name: t('allForms', { ns: 'labels' }) || 'Tutti i form' }, ...formList]}
                                            value="Code"
                                            text="Name"
                                            className="inputStyle blackText bg-white"
                                            selected={searchFilters.formId ? [{ Code: searchFilters.formId }] : [{ Code: "" }]}
                                            onChange={(e) => {
                                                const selected = e.selected?.[0]?.Code || "";
                                                handleFilterChange('formId', selected);
                                            }}
                                            placeholder={t('selectForm', { ns: 'labels' })}
                                            dropDownParent="root"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('requiresValidation', { ns: 'labels' })}
                                        </label>
                                        <select 
                                            className="form-control"
                                            value={searchFilters.requiresValidation === null ? "" : searchFilters.requiresValidation.toString()}
                                            onChange={(e) => {
                                                const value = e.target.value === "" ? null : e.target.value === "true";
                                                handleFilterChange('requiresValidation', value);
                                            }}
                                        >
                                            <option value="">{t('allValidationTypes', { ns: 'labels' }) || 'Tutti i tipi'}</option>
                                            <option value="true">{t('yes', { ns: 'labels' })}</option>
                                            <option value="false">{t('no', { ns: 'labels' })}</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('searchDateFrom', { ns: 'labels' }) || 'Data inizio da'}
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={searchFilters.startDateFrom}
                                            onChange={(e) => handleFilterChange('startDateFrom', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('searchDateTo', { ns: 'labels' }) || 'Data fine entro'}
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={searchFilters.endDateBefore}
                                            onChange={(e) => handleFilterChange('endDateBefore', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-4 d-flex align-items-end">
                                        <div className="form-check me-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="includeInactive"
                                                checked={searchFilters.includeInactive}
                                                onChange={(e) => handleFilterChange('includeInactive', e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="includeInactive">
                                                {t('includeInactive', { ns: 'labels' }) || 'Includi inattivi'}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Pulsanti azioni */}
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <button
                                            type="button"
                                            className="btn btn-primary me-2"
                                            onClick={() => searchAssignments()}
                                            disabled={searchLoading}
                                        >
                                            {searchLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {t('searching', { ns: 'labels' }) || 'Ricerca...'}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-search me-2"></i>
                                                    {t('search', { ns: 'labels' })}
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={handleClearFilters}
                                            disabled={searchLoading}
                                        >
                                            {t('clear', { ns: 'labels' })}
                                        </button>
                                    </div>
                                </div>

                                {/* Risultati ricerca */}
                                <div className="mt-4">
                                    {assignments.length === 0 ? (
                                        <div className="text-center py-4">
                                            <i className="fa-solid fa-info-circle fa-2x text-muted mb-3"></i>
                                            <h6 className="text-muted">{t('noAssignmentsFound', { ns: 'labels' }) || 'Nessuna assegnazione trovata'}</h6>
                                            <p className="text-muted mb-0">
                                                {t('useFiltersToSearch', { ns: 'labels' }) || 'Utilizza i filtri sopra per cercare le assegnazioni e clicca "Cerca".'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="card">
                                            <div className="card-body p-0">
                                                <div className="active-projects task-table">
                                                    <div className="tbl-caption">
                                                        <h4 className="heading mb-0 text-start">
                                                            {t('searchResults', { ns: 'labels' }) || 'Risultati ricerca'} 
                                                            {assignments.length > 0 && (
                                                                <span className="badge bg-primary ms-2">{assignments.length}</span>
                                                            )}
                                                        </h4>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-striped table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ 
                                                                        wordWrap: 'break-word',
                                                                        whiteSpace: 'normal',
                                                                        maxWidth: '150px',
                                                                        minWidth: '120px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('form', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        wordWrap: 'break-word',
                                                                        whiteSpace: 'normal',
                                                                        maxWidth: '130px',
                                                                        minWidth: '100px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('assignedTo', { ns: 'labels' }) || 'Assegnato a'}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '60px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('assignmentType', { ns: 'labels' }) || 'Tipo'}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '80px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('startDate', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '80px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('endDate', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '50px',
                                                                        fontSize: '0.875rem',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {t('cycles', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '70px',
                                                                        fontSize: '0.875rem',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {t('requiresValidation', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '60px',
                                                                        fontSize: '0.875rem',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {t('status', { ns: 'labels' }) || 'Stato'}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '80px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('createdDate', { ns: 'labels' }) || 'Data creazione'}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '80px',
                                                                        fontSize: '0.875rem',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {t('actions', { ns: 'labels' }) || 'Azioni'}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {assignments.map((assignment) => (
                                                                    <tr key={assignment.Id}>
                                                                        <td style={{
                                                                            wordWrap: 'break-word',
                                                                            wordBreak: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                            maxWidth: '150px',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            <strong>{getFormName(assignment.FormId)}</strong>
                                                                        </td>
                                                                        <td style={{
                                                                            wordWrap: 'break-word',
                                                                            wordBreak: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                            maxWidth: '130px',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.UserId ? getUserName(assignment.UserId) : getGroupName(assignment.GroupId)}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.UserId ? t('user', { ns: 'labels' }) || 'Utente' : t('group', { ns: 'labels' }) || 'Gruppo'}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {formatDate(assignment.StartDate)}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {formatDate(assignment.EndDate)}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap', 
                                                                            textAlign: 'center',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.Cycles || 1}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap', 
                                                                            textAlign: 'center',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.ToValidate ? t('yes', { ns: 'labels' }) : t('no', { ns: 'labels' })}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap', 
                                                                            textAlign: 'center',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.Status}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {formatDate(assignment.ItemCreated)}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem',
                                                                            textAlign: 'center'
                                                                        }}>
                                                                            <a
                                                                                className="text-primary fs-13"
                                                                                title={t('edit', { ns: 'labels' }) || 'Modifica'}
                                                                                role="button"
                                                                                onClick={() => openEditModal(assignment)}
                                                                                style={{ 
                                                                                    display: 'inline-block',
                                                                                    padding: '2px 4px',
                                                                                    minWidth: '30px',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                            >
                                                                                <i className="fa-solid fa-edit fa-lg"></i>
                                                                            </a>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Assignment Modal */}
            {showEditModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {t('editAssignment', { ns: 'labels' }) || 'Modifica Assegnazione'}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeEditModal}></button>
                            </div>
                            <div className="modal-body">
                                {editingAssignment && (
                                    <>
                                        <div className="alert alert-info">
                                            <i className="fa-solid fa-info-circle me-2"></i>
                                            {t('editAssignmentInfo', { ns: 'labels' }) || 'Modifica i dettagli dell\'assegnazione selezionata.'}
                                        </div>

                                        <ul className="nav nav-tabs" id="edit-assign-tab" role="tablist">
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link ${editForm.activeTab === 'user' ? 'active' : ''}`} 
                                                        id="edit-user-tab" 
                                                        onClick={() => setEditForm({...editForm, activeTab: 'user'})} 
                                                        type="button" 
                                                        role="tab" 
                                                        aria-controls="edit-user-tab-pane" 
                                                        aria-selected={editForm.activeTab === 'user'}>
                                                    {t('assignToUser', { ns: 'labels' })}
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link ${editForm.activeTab === 'group' ? 'active' : ''}`} 
                                                        id="edit-group-tab" 
                                                        onClick={() => setEditForm({...editForm, activeTab: 'group'})} 
                                                        type="button" 
                                                        role="tab" 
                                                        aria-controls="edit-group-tab-pane" 
                                                        aria-selected={editForm.activeTab === 'group'}>
                                                    {t('assignToGroup', { ns: 'labels' })}
                                                </button>
                                            </li>
                                        </ul>

                                        <div className="tab-content pt-3" id="edit-assign-tab-content">
                                            <div className={`tab-pane fade ${editForm.activeTab === 'user' ? 'show active' : ''}`} 
                                                 id="edit-user-tab-pane" 
                                                 role="tabpanel" 
                                                 aria-labelledby="edit-user-tab">
                                                <label className="form-label">
                                                    {t('selectUser', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={users}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={editForm.selectedUser ? [{ Code: editForm.selectedUser }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        setEditForm({...editForm, selectedUser: selected});
                                                    }}
                                                    placeholder={t('selectUser', { ns: 'labels' })}
                                                />
                                            </div>
                                            <div className={`tab-pane fade ${editForm.activeTab === 'group' ? 'show active' : ''}`} 
                                                 id="edit-group-tab-pane" 
                                                 role="tabpanel" 
                                                 aria-labelledby="edit-group-tab">
                                                <label className="form-label">
                                                    {t('selectGroup', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={groups}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={editForm.selectedGroup ? [{ Code: editForm.selectedGroup }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        setEditForm({...editForm, selectedGroup: selected});
                                                    }}
                                                    placeholder={t('selectGroup', { ns: 'labels' })}
                                                />
                                            </div>
                                        </div>

                                        <div className="row g-3 mt-2">
                                            <div className="col-md-12">
                                                <label className="form-label">
                                                    {t('selectForm', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={formList}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={editForm.selectedForm ? [{ Code: editForm.selectedForm }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        setEditForm({...editForm, selectedForm: selected});
                                                    }}
                                                    placeholder={t('selectForm', { ns: 'labels' })}
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('cycles', { ns: 'labels' }) || 'Cicli'}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={editForm.cycles}
                                                    onChange={(e) => setEditForm({...editForm, cycles: parseInt(e.target.value) || 1})}
                                                    min="1"
                                                    max="100"
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('expectedStartDate', { ns: 'labels' })}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={editForm.expectedCompilationStartDate}
                                                    onChange={(e) => setEditForm({...editForm, expectedCompilationStartDate: e.target.value})}
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('expectedEndDate', { ns: 'labels' })}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={editForm.expectedCompilationEndDate}
                                                    onChange={(e) => setEditForm({...editForm, expectedCompilationEndDate: e.target.value})}
                                                />
                                            </div>

                                            <div className="col-12">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="editRequiresValidation"
                                                        checked={editForm.requiresValidation}
                                                        onChange={(e) => setEditForm({...editForm, requiresValidation: e.target.checked})}
                                                    />
                                                    <label className="form-check-label" htmlFor="editRequiresValidation">
                                                        {t('requiresValidation', { ns: 'labels' })}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeEditModal}
                                    disabled={saving}
                                >
                                    {t('cancel', { ns: 'labels' }) || 'Annulla'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger me-2"
                                    onClick={handleDeleteAssignment}
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            {t('deleting', { ns: 'labels' }) || 'Eliminazione...'}
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-trash me-2"></i>
                                            {t('delete', { ns: 'labels' }) || 'Elimina'}
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveChanges}
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            {t('saving', { ns: 'labels' })}
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-save me-2"></i>
                                            {t('saveChanges', { ns: 'labels' }) || 'Salva modifiche'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export { Form_Assignment_Screen };
